import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { getAuthCallbackUrl } from "@/lib/auth";
import AuthShell from "@/pages/auth/components/AuthShell";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${getAuthCallbackUrl()}?next=/auth/reset-password`,
    });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSent(true);
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setVerifyLoading(true);
    setError("");
    const { error: err } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "recovery",
    });
    setVerifyLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    navigate("/auth/reset-password");
  }

  return (
    <AuthShell
      title="Mot de passe oublié"
      subtitle="Nous vous enverrons un code pour le réinitialiser."
    >
      {!sent ? (
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
              className={inputClass}
            />
          </div>

          {error && <p className="text-sm text-primary-600">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Envoi…
              </>
            ) : (
              "Envoyer le code"
            )}
          </button>

          <p className="text-sm text-center text-foreground-600 pt-1">
            <Link
              to="/auth/login"
              className="text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              Retour à la connexion
            </Link>
          </p>
        </form>
      ) : (
        <form onSubmit={handleVerify} className="space-y-4">
          <p className="text-sm text-foreground-600">
            Un code de réinitialisation a été envoyé à{" "}
            <span className="font-semibold text-foreground-900">{email}</span>.
          </p>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Code de réinitialisation
            </label>
            <input
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className={inputClass}
            />
          </div>

          {error && <p className="text-sm text-primary-600">{error}</p>}

          <button
            type="submit"
            disabled={verifyLoading}
            className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
          >
            Continuer
          </button>
        </form>
      )}
    </AuthShell>
  );
}